import { useEffect, useState, type ReactNode } from 'react'
import { useLocation, Navigate } from 'react-router'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'

interface Props {
  children: ReactNode
}

type Status = 'checking' | 'pass' | 'need-aal2' | 'need-enrollment'

/**
 * Wraps routes that require AAL2 when the user has a verified TOTP factor.
 *
 * Logic:
 *  - If user has no verified factor → pass through (not enrolled, no AAL2 requirement)
 *  - If user has a verified factor but current session is AAL1 → redirect /mfa/challenge
 *  - If org has mfa_required=true and user has NO verified factor → redirect /mfa/enroll
 *  - Otherwise → render children
 */
export function MfaRequiredGuard({ children }: Props) {
  const { user, activeOrg } = useAuth()
  const location = useLocation()
  const [status, setStatus] = useState<Status>('checking')

  useEffect(() => {
    let cancelled = false

    async function check() {
      if (!user) {
        if (!cancelled) setStatus('pass')
        return
      }

      const [assuranceRes, factorsRes] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ])

      if (cancelled) return

      const aal = assuranceRes.data
      const verifiedFactors = factorsRes.data?.totp?.filter(f => f.status === 'verified') ?? []
      const hasVerifiedFactor = verifiedFactors.length > 0

      if (hasVerifiedFactor) {
        // User is enrolled — enforce AAL2
        if (aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2') {
          setStatus('need-aal2')
          return
        }
      } else if (activeOrg) {
        // Check if org mandates MFA
        const { data: orgData } = await supabase
          .from('organizations')
          .select('mfa_required')
          .eq('id', activeOrg.organization_id)
          .single()

        if (!cancelled && orgData?.mfa_required) {
          setStatus('need-enrollment')
          return
        }
      }

      if (!cancelled) setStatus('pass')
    }

    check()
    return () => { cancelled = true }
  }, [user, activeOrg, location.pathname])

  if (status === 'checking') {
    // Render nothing while checking — the parent Suspense/ProtectedRoute already shows a loader
    return null
  }

  if (status === 'need-aal2') {
    const challengeUrl = `/mfa/challenge?redirect=${encodeURIComponent(location.pathname + location.search)}`
    return <Navigate to={challengeUrl} replace />
  }

  if (status === 'need-enrollment') {
    // The enrollment surface must live OUTSIDE this guard. The old target
    // (/settings?tab=security&mfa_required=1) was itself guard-wrapped, so the
    // guard re-fired there and settled into rendering <Navigate> forever —
    // app chrome with a blank content area on every page, and no way to enroll.
    const enrollUrl = `/mfa/enroll?redirect=${encodeURIComponent(location.pathname + location.search)}`
    return <Navigate to={enrollUrl} replace />
  }

  return <>{children}</>
}
